import { useCallback, useState } from 'react';

interface Testimonial {
  base: string; // file base in /public (served as /<base>.webp + .png fallback)
  alt: string; // transcript-based alt for screen readers + SEO
}

// Handwritten, anonymized client testimonials. Alt text = the transcribed note
// so screen readers + search engines can read these image-only testimonials.
// Student name in #1 is intentionally omitted to keep it anonymized.
const TESTIMONIALS: Testimonial[] = [
  {
    base: 'megan_testimony_1',
    alt: `Handwritten testimonial: "I can't tell you how much it means to me that you have been there for my daughter and helped to encourage her. You gave her strength, love, and support when she needed it the most. I can't thank you enough for working with me to help her see her true potential!" (Parent of a student)`,
  },
  {
    base: 'megan_testimony_2',
    alt: `Handwritten testimonial: "Thank you for helping me grow into such a strong young woman. You have helped me achieve my dreams and believe in myself. You believed in me and gave me the strength to keep going and not give up. I couldn't have done this without you. To me you aren't just a counselor. You are someone I can trust, someone I can talk to to get things off my mind. Thank you so much for being who you are. You have truly changed my life." (Student)`,
  },
  {
    base: 'megan_testimony_3',
    alt: `Handwritten testimonial: "I just would like a moment to thank you for all you do to help all students here at CHS! You really helped me get through freshman year, and I just can't thank you enough. We all appreciate you!" (Student)`,
  },
  {
    base: 'megan_testimony_4',
    alt: `Handwritten testimonial: "Thank you for never giving up on me and for reminding me how great I am. You showed me that I could do anything I put my mind to, and you never judged me no matter the situation. Thank you for being the rainbow after the storm and my sunshine to guide the way." (Student)`,
  },
  {
    base: 'megan_testimony_5',
    alt: `Handwritten testimonial: "At first, I thought you were just another counselor who probably wouldn't be able to help me. But once I got to know you and realized how much you truly listened, you became my favorite counselor. You helped me through traumatic events and difficult personal challenges, and I can't thank you enough for always being there for me. Thank you for listening, caring, and being so genuine. You truly care about your students, and that made all the difference. I will never forget the impact you've had on my life." (Student)`,
  },
];

export function TestimonialCarousel() {
  const [index, setIndex] = useState(0);
  const count = TESTIMONIALS.length;
  const go = useCallback((to: number) => setIndex((to + count) % count), [count]);

  return (
    <div
      role="group"
      aria-roledescription="carousel"
      aria-label="Family testimonials"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') go(index - 1);
        if (e.key === 'ArrowRight') go(index + 1);
      }}
      className="relative mx-auto max-w-[44rem] rounded-[var(--radius-lg)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-sage-deep)] focus-visible:ring-offset-4"
    >
      {/* viewport */}
      <div className="overflow-hidden">
        <div
          className="flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${index * 100}%)` }}
        >
          {TESTIMONIALS.map((t, idx) => (
            <div
              key={t.base}
              className="flex h-[70vh] max-h-[40rem] w-full shrink-0 items-center justify-center px-2"
              aria-hidden={idx !== index}
            >
              <picture className="flex h-full w-full items-center justify-center">
                <source srcSet={`/${t.base}.webp`} type="image/webp" />
                <img
                  src={`/${t.base}.png`}
                  alt={t.alt}
                  loading="lazy"
                  className="max-h-full max-w-full rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white object-contain shadow-[var(--shadow-cta)]"
                />
              </picture>
            </div>
          ))}
        </div>
      </div>

      {/* arrows */}
      <button
        type="button"
        onClick={() => go(index - 1)}
        aria-label="Previous testimonial"
        className="absolute left-1 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-cream)]/90 text-xl text-[var(--color-ink)] shadow-[var(--shadow-cta)] transition-colors hover:bg-[var(--color-cream)] sm:-left-3"
      >
        <span aria-hidden>&larr;</span>
      </button>
      <button
        type="button"
        onClick={() => go(index + 1)}
        aria-label="Next testimonial"
        className="absolute right-1 top-1/2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full border border-[var(--color-border)] bg-[var(--color-cream)]/90 text-xl text-[var(--color-ink)] shadow-[var(--shadow-cta)] transition-colors hover:bg-[var(--color-cream)] sm:-right-3"
      >
        <span aria-hidden>&rarr;</span>
      </button>

      {/* dots */}
      <div className="mt-6 flex items-center justify-center gap-2.5">
        {TESTIMONIALS.map((t, idx) => (
          <button
            key={t.base}
            type="button"
            onClick={() => go(idx)}
            aria-label={`Go to testimonial ${idx + 1} of ${count}`}
            aria-current={idx === index}
            className={`size-2.5 rounded-full transition-colors ${
              idx === index
                ? 'bg-[var(--color-sage-deep)]'
                : 'bg-[var(--color-border)] hover:bg-[var(--color-ink-soft)]'
            }`}
          />
        ))}
      </div>
    </div>
  );
}
